import { useGetAuction, useGetPatientQuery } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { useSenderAccountContext } from "context/SenderAccountContext";
import { PATIENT_TAB_SEARCH, QUERY_PROGRESS_SUCCEED } from "core/consts";
import { hasAccessToDoctorDashboard } from "core/model/accounts";
import { useIntParams } from "core/model/utils/urls";
import { Auction, AuctionNoPatient, CareseekerRole, Patient } from "core/types";
import { BaseEventSchema } from "core/validationSchemas/tracking";
import { RedirectButton } from "dsl/atoms/RedirectButton";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { usePageMetaData } from "dsl/hooks";
import { PatientEncryptionProvider } from "dsl/organisms/Encryption/Patient";
import { PatientMenuStatic } from "dsl/organisms/PatientMenu";
import DoctorPatientMenu from "dsl/organisms/PatientMenu/DoctorPatientMenu";
import { ReactNode, useCallback, useEffect } from "react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { useTracking } from "react-tracking";
import {
  useAppDispatch,
  useAppSelector,
  useConnecterWithCareseeker,
  useLoggedInAccount,
} from "reduxentities/hooks";
import { selectCareseekerRoles } from "reduxentities/selectors";
import { CARESEEKER_CHANGED } from "reduxentities/slices/authSlice";
import { useTranslations } from "translations";
import ErrorPage from "../ErrorPage";
import PatientProfile from "../PatientProfile";

type PatientHomeContext = {
  auction: Auction;
  patient: Patient;
};

type PatientProfileIndexContext = {
  patient: Patient;
};

export function usePatientHomeContext() {
  const context = useOutletContext<PatientHomeContext>();
  if (!context) {
    throw new Error(
      "usePatientHomeContext must be used within a PatientHomeProvider",
    );
  }
  return context;
}

export function usePatientContext(): Patient {
  const context = useOutletContext<{ patient: Patient }>();
  if (!context?.patient) {
    throw new Error("usePatientContext must be used within a patient outlet");
  }
  return context.patient;
}

export function PatientProfileIndex() {
  const context = useOutletContext<PatientProfileIndexContext>();
  const patient = context?.patient;

  if (!patient?.auctions?.length) {
    return <NoAuctionsRedirectToDashboard patientId={patient?.id} />;
  }

  const defaultAuction = patient.auctions[0];
  const auctionWithPatient: Auction = {
    ...defaultAuction,
    patient,
  };

  return (
    <PatientHomeContext auction={defaultAuction} patient={patient}>
      <PatientProfile auction={auctionWithPatient} />
    </PatientHomeContext>
  );
}

function usePatientPageTitle(path: string, patient: Patient | undefined) {
  const translations = useTranslations();

  const patientIdentifier = patient?.user_id;
  const getNewTitle = useCallback(
    (titleBase: string) =>
      `${translations.patient.patient} ${
        patientIdentifier ? `${patientIdentifier}` : ""
      }${titleBase ? ` ${titleBase}` : ""}`,
    [patientIdentifier],
  );

  const pageMap: { [key: string]: string } = {
    profile: translations.patient.profile,
    search: translations.search.search,
    timeline: translations.timeline.title,
  };

  const pageKey = Object.keys(pageMap).find((key) => path.includes(key));

  usePageMetaData({
    title: () => getNewTitle((pageKey && pageMap[pageKey]) || ""),
  });
}

export function PatientHomeMenu({ auction }: { auction?: Auction }) {
  if (!auction) return null;

  return <PatientMenuStatic auction={auction} />;
}

function PatientHomeContext({
  auction,
  children,
  patient,
}: {
  auction: AuctionNoPatient | undefined;
  children: ReactNode;
  patient: Patient | undefined;
}) {
  const { Track } = useTracking<DeepPartial<BaseEventSchema>>({
    user_information: { auction_id: auction?.id },
  });

  return (
    <Track>
      <PatientEncryptionProvider patient={patient}>
        {children}
      </PatientEncryptionProvider>
    </Track>
  );
}

function PatientHome({
  auctionId,
  patient,
}: {
  auctionId: number;
  patient: Patient;
}) {
  const [queryProgress, auction] = useGetAuction(
    auctionId ?? patient.auctions?.[0].id,
  );

  usePatientPageTitle(location.pathname, auction?.patient);
  const translations = useTranslations();
  const { careseekerId } = useConnecterWithCareseeker();
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectCareseekerRoles);

  // Check if the patient belongs to a different
  // clinic with a role in the current account.
  // If that is the case, dispatch a role switch.
  useEffect(() => {
    const patientCareseekerId = patient.careseeker?.id;
    if (patientCareseekerId === careseekerId) return;

    const country = patient.careseeker?.address?.country;
    const inRoles = roles?.some(
      (role: CareseekerRole) => role?.careseeker?.id === patientCareseekerId,
    );

    if (inRoles) {
      dispatch(
        CARESEEKER_CHANGED({
          careseeker_id: patientCareseekerId,
          careseeker_name: patient.careseeker?.name,
          country,
        }),
      );
    }
  }, [patient.id, careseekerId]);

  return (
    <Result
      data={auction}
      ErrorComponent={
        <RedirectButton
          label={translations.actions.backToDashboard}
          url="/dashboard/todo/active"
        />
      }
      getErrorLabel={(translations) => translations.patient.errorLoading}
      id="patient-home-auction"
      queryProgress={queryProgress}
    >
      {(auction) => (
        <Outlet
          context={{
            auction,
            patient: auction.patient,
          }}
        />
      )}
    </Result>
  );
}

export default function ConnectedPatientHome() {
  const translations = useTranslations();
  const [searchParams] = useSearchParams();
  const { auctionId, patientId } = useIntParams<{
    auctionId: number | undefined;
    patientId: number;
  }>({
    patientId: -1,
  });
  const { Track } = useTracking<DeepPartial<BaseEventSchema>>({
    user_information: { auction_id: auctionId, patient_id: patientId },
  });

  const { account, activeRoles } = useSenderAccountContext();
  const hasDoctorDashboardOnly = hasAccessToDoctorDashboard(
    account,
    activeRoles,
  );

  const [queryProgress, patient] = useGetPatientQuery({
    decrypt: true,
    patientId,
  });

  return (
    <Track>
      <Result
        data={hasDoctorDashboardOnly ? ({ id: patientId } as Patient) : patient}
        ErrorComponent={
          <RedirectButton
            label={translations.actions.backToDashboard}
            url={
              hasDoctorDashboardOnly ? "/dashboard" : "/dashboard/todo/active"
            }
          />
        }
        getErrorLabel={(translations) => translations.patient.errorLoading}
        id="patient-home-patient"
        queryProgress={
          hasDoctorDashboardOnly ? QUERY_PROGRESS_SUCCEED : queryProgress
        }
      >
        {(patient) => {
          if (hasDoctorDashboardOnly) {
            return (
              <>
                <DoctorPatientMenu patient={patient} />
                <div className="min-h-below-navbar-patient-menu flex w-full flex-col bg-gray-50 p-4">
                  <Outlet context={{ patient }} />
                </div>
              </>
            );
          }

          if (!patient.auctions?.length) {
            return <NoAuctionsRedirectToDashboard patientId={patient?.id} />;
          }

          if (!auctionId) {
            const defaultAuction = patient?.auctions?.[0];
            return (
              <Navigate
                replace
                to={`/app/clinic/dashboard/patient/${patientId}/auction/${defaultAuction.id}/${PATIENT_TAB_SEARCH}?${searchParams}`}
              />
            );
          }

          const auction = patient.auctions?.find(({ id }) => id === auctionId);
          if (!auction) {
            return (
              <ErrorPage
                ErrorComponent={
                  <RedirectButton
                    label={translations.actions.backToDashboard}
                    url="/dashboard/todo/active"
                  />
                }
                getErrorLabel={(translations) =>
                  translations.patient.errorLoadingUrlChanges
                }
              />
            );
          }
          return (
            <PatientHomeContext auction={auction} patient={patient}>
              <PatientHome auctionId={auction.id} patient={patient} />
            </PatientHomeContext>
          );
        }}
      </Result>
    </Track>
  );
}

function NoAuctionsRedirectToDashboard({
  patientId,
}: {
  patientId: number | undefined;
}) {
  const account = useLoggedInAccount();
  const navigate = useNavigate();
  const toast = useToast();
  const translations = useTranslations();

  useEffect(() => {
    console.error(
      `No auctions redirect - account ${account?.id} - patient ${patientId}`,
    );
    toast({
      color: "danger",
      message: translations.patient.noAuctionsError,
    });
    setTimeout(() => {
      navigate("/app/clinic/dashboard");
    }, 0);
  }, [toast, translations, navigate]);

  return null;
}
